import React, { useState } from 'react';
import { useToast } from '../hooks/use-toast';
import { Mail, CheckCircle, ArrowRight, Shield } from 'lucide-react';

const NewsletterSignup = ({ variant = 'default', className = '' }) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    source: 'website',
    marketing_consent: true
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/newsletter-signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          marketing_consent: formData.marketing_consent
        })
      });

      if (response.ok) {
        const result = await response.json();
        toast({
          title: "Successfully Subscribed!",
          description: result.message,
          variant: "default"
        });
        
        // Reset form
        setFormData({
          email: '',
          full_name: '',
          source: 'website',
          marketing_consent: true
        });
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to subscribe');
      }
    } catch (error) {
      toast({
        title: "Subscription Failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Compact variant for footer/sidebar
  if (variant === 'compact') {
    return (
      <div className={`bg-orange-50 rounded p-4 ${className}`}>
        <div className="flex items-center gap-2 mb-3">
          <Mail className="h-5 w-5 text-orange-600" />
          <h3 className="font-bold text-black uppercase">Newsletter</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Get weekly updates when new stock is added
        </p>
        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            placeholder="Your email address"
          />

          
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-orange-600 text-white py-2 rounded text-sm font-bold hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Subscribing...' : 'Subscribe'}
          </button>
        </form>
      </div>
    );
  }

  // Inline variant for page content
  if (variant === 'inline') {
    return (
      <div className={`bg-gray-100 rounded p-6 ${className}`}>
        <div className="max-w-2xl mx-auto text-center">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="flex-1 border border-gray-300 rounded px-4 py-3"
                placeholder="Enter your email address"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-orange-600 text-white px-6 py-3 rounded font-bold hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <span>Subscribing...</span>
                ) : (
                  <>
                    Subscribe
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </div>

          </form>
        </div>
      </div>
    );
  }

  // Default full variant
  return (
    <div className={`bg-white border border-gray-200 rounded shadow-lg p-8 ${className}`}>
      <div className="text-center mb-6">
        <div className="bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
          <Mail className="h-8 w-8 text-orange-600" />
        </div>
        <h2 className="text-2xl font-black text-black mb-2 font-inter uppercase">
          JOIN OUR <span className="text-orange-600">NEWSLETTER</span>
        </h2>
        <p className="text-gray-600">
          Get weekly updates when new stock is added
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-bold text-gray-700 mb-2 uppercase">
            Email Address *
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="w-full border border-gray-300 rounded px-4 py-3 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            placeholder="your@email.com"
          />
        </div>

        <div>
          <label className="block text-sm font-bold text-gray-700 mb-2 uppercase">
            Full Name (Optional)
          </label>
          <input
            type="text"
            name="full_name"
            value={formData.full_name}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded px-4 py-3 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            placeholder="John Smith"
          />
        </div>



        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-orange-600 text-white py-3 rounded font-bold hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 uppercase"
        >
          {isSubmitting ? (
            <span className="flex items-center gap-2">
              <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
              Subscribing...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              Subscribe to Newsletter
            </span>
          )}
        </button>

        <div className="text-center">
          <p className="text-xs text-gray-500">
            By subscribing, you agree to receive marketing emails from Lawman Commercials. 
            You can unsubscribe at any time. We respect your privacy.
          </p>
        </div>
      </form>

      {/* Benefits */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <h4 className="font-bold text-sm mb-3 uppercase">What You'll Get:</h4>
        <ul className="text-sm text-gray-600 space-y-2">
          <li className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-orange-600 flex-shrink-0" />
            Weekly updates on new stock arrivals
          </li>
        </ul>
      </div>
    </div>
  );
};

export default NewsletterSignup;